// Vendor
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

// Bernie
import { AnalyticsStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { ILocalization, LocalizedText, LocalizedTextProps } from 'bernie-l10n';

// UITK
import { UitkBanner } from 'uitk-react-banner';
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkDialogAnimationType, UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkPageLoader } from 'uitk-react-loader';
import { UitkLinkButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkToolbar } from 'uitk-react-toolbar';

// Stores
import { MultiItemStore } from 'stores/multi-item-store';
import { MultiItemStoreStatus } from 'stores/multi-item-store-util';
import { SearchResultsStore } from 'stores/search-results-store';

// Models
import { InCartProperty } from 'models/multi-item/multi-item-types';

// Components
import { HoldingAreaActions } from 'components/multi-item/holding-area-actions';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';

// Utils
import { generateAddAnotherPropertyURL } from './utils';

const LinkButtonTracked = ClickTracker(UitkLinkButton);
const UitkSecondaryButtonTracked = ClickTracker(UitkSecondaryButton);

const guestSummaryMessage = (rooms: number, guests: number): LocalizedTextProps => {
  if (rooms === 1) {
    if (guests === 1) {
      return {
        message: 'multiProperty.guestSummary.singleRoomSingleGuest'
      };
    }

    return {
      message: 'multiProperty.guestSummary.singleRoomMultipleGuests',
      data: [guests]
    };
  }

  return {
    message: 'multiProperty.guestSummary.multipleRoomsMultipleGuests',
    data: [rooms, guests]
  };
};

export interface CartItemsProps {
  cartItemDeleter: (id) => () => void;
  removeText: string;
  isFirstItem: boolean;
  analytics?: AnalyticsStore;
  itemIndex?: number;
}

const InCartListing = ({
  adults,
  cartItemDeleter,
  children,
  id,
  image,
  isFirstItem,
  name,
  ratePlan,
  removeText,
  rooms,
  analytics,
  itemIndex
}: InCartProperty & CartItemsProps): JSX.Element => {
  const getDisplayPrice = () => {
    if (!ratePlan || !ratePlan.available || !ratePlan.leadPrice || !ratePlan.leadPrice.formatted) {
      return null;
    }

    if (hasLoyaltyPoints()) {
      return ratePlan.priceAfterLoyaltyPointsApplied.formatted;
    }

    return ratePlan.leadPrice.formatted;
  };

  const hasLoyaltyPoints = () =>
    isFirstItem && ratePlan.priceAfterLoyaltyPointsApplied && ratePlan.priceAfterLoyaltyPointsApplied.formatted;

  const fees = ratePlan && ratePlan.fees;
  const trackRemoveSectionClick = () => {
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('MultiProp.ClickedRemoveSection', 'MultiProp Property Remove Attempt', analytics.omnitureData, null, true);
    }
  };

  return (
    <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED} withBorder={true}>
      <UitkCardMedia>
        <UitkFigure ratio={UitkFigureAspectRatioType.R3_2}>
          <UitkImage alt={name} src={image} />
        </UitkFigure>
      </UitkCardMedia>
      <UitkCardContent>
        <UitkCardTextSegment className="uitk-cell all-cell-fill">
          <h3 className="uitk-cell uitk-type-400 pwa-theme--grey-900 truncate" data-stid="content-hotel-title">
            {name}
          </h3>
          {getDisplayPrice() ? (
            <div>
              <div className="uitk-cell uitk-type-200 truncate">{ratePlan.roomName}</div>
              <div className="uitk-cell uitk-type-200">
                <LocalizedText {...guestSummaryMessage(rooms, adults + children)} />
              </div>
              <div className="uitk-cell uitk-type-300 uitk-type-bold all-t-padding-two">
                <LocalizedText message="multiProperty.price.perNight" data={[getDisplayPrice()]} />
              </div>
              <div className="uitk-type-100">
                <LocalizedText message="multiProperty.priceSummary.perRoom" />
              </div>
              {hasLoyaltyPoints() && (
                <div className="uitk-type-100">
                  <LocalizedText message="common.pointsApplied" />
                </div>
              )}
              {fees &&
                fees.length > 0 && (
                  <div className="all-t-padding-two">
                    {fees.map((fee, index) => (
                      <div key={index} className="uitk-cell uitk-type-100">
                        {fee}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ) : (
            <div className="uitk-cell uitk-type-200  pwa-theme--urgency all-t-padding-two">
              <LocalizedText message="multiProperty.playback.propertyNotAvailable" />
            </div>
          )}
        </UitkCardTextSegment>
      </UitkCardContent>
      <section className="uitk-cell all-cell-1-1 uitk-grid uitk-type-left uitk-card-separator-top" onClick={trackRemoveSectionClick}>
        <LinkButtonTracked
          className="uitk-cell all-cell-shrink all-x-padding-two all-y-padding-two"
          onClick={cartItemDeleter(id)}
          rfrr={`MultiProp.RemovePropertyClick.${itemIndex}`}
          linkName="Multi Property remove button"
        >
          <span>{removeText}</span>
        </LinkButtonTracked>
      </section>
    </UitkCard>
  );
};

export interface HoldingAreaContentProps {
  analytics?: AnalyticsStore;
  cartItemDeleter: (id) => () => void;
  closeDialog: () => void;
  getCartItems: () => Promise<InCartProperty[]>;
  goToCheckout: () => void;
  isAllSoldOut: () => boolean;
  l10n: ILocalization;
  multiItem?: MultiItemStore;
  searchCriteria: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
}

@inject('analytics', 'multiItem', 'searchResults')
@observer
export class HoldingAreaContent extends React.Component<HoldingAreaContentProps> {
  public render() {
    const {
      analytics,
      cartItemDeleter,
      closeDialog,
      getCartItems,
      goToCheckout,
      isAllSoldOut,
      l10n,
      multiItem,
      searchCriteria,
      searchResults
    } = this.props;

    const titleText = l10n.formatText(
      multiItem.itemCount === 1 ? 'multiProperty.playback.titleSingle' : 'multiProperty.playback.titleMultiple',
      multiItem.itemCount
    );
    const { items, error, status } = multiItem;

    const formattedDate = date => l10n.formatDateString(date, { skeleton: 'MMMEd' });
    const processing = [
      MultiItemStoreStatus.FETCHING,
      MultiItemStoreStatus.PREPARE_CHECKOUT_PROCESSING,
      MultiItemStoreStatus.PREPARE_CHECKOUT_SUCCESS
    ].includes(status);

    const addMoreProperties = generateAddAnotherPropertyURL(searchCriteria, searchResults);

    return (
      <UitkFullscreenDialog openCallback={getCartItems} dialogShow={true} animation={UitkDialogAnimationType.FADE}>
        <UitkToolbar
          action={closeDialog}
          analytics={analytics.setupTracker('MultiProp', 'Collapse Holding Area')}
          header={titleText}
          iconLabel={l10n.formatText('hotels.infosite.paymentOptions.cancelButton.a11yLabel')}
        />
        <UitkDialogContent className="all-b-padding-twelve" zeroPadding={true}>
          <div className="all-b-padding-twelve">
            <UitkPageLoader visible={processing} />
            {error && error.message && <UitkBanner fullWidth={true} {...error} />}
            <ul className="uitk-list">
              {items &&
                items.map(
                  (item, index) =>
                    item &&
                    item.id && (
                      <li key={item.id} className="all-x-padding-three all-t-padding-three">
                        <h3 className="all-b-padding-two">
                          {`${item.city}: ${formattedDate(item.checkin)} - ${formattedDate(item.checkout)}`}
                        </h3>
                        <InCartListing
                          {...item}
                          cartItemDeleter={cartItemDeleter}
                          isFirstItem={index === 0}
                          removeText={l10n.formatText('remove')}
                          analytics={analytics}
                          itemIndex={index}
                        />
                      </li>
                    )
                )}
            </ul>
            <UitkSecondaryButtonTracked
              className="all-b-margin-four all-t-margin-six all-l-margin-three"
              icon="add"
              linkName="Add Another Property"
              rfrr="MultiProp.AddProperty"
              tag={Link}
              text={l10n.formatText('multiProperty.playback.addAnotherMessage')}
              to={addMoreProperties}
            />
          </div>
        </UitkDialogContent>
        <HoldingAreaActions goToCheckout={goToCheckout} l10n={l10n} isAllSoldOut={isAllSoldOut} />
      </UitkFullscreenDialog>
    );
  }
}
